import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router';

import { SIDE_BAR_ACTION_ROUTE } from 'pages/Disruption/SupplyChainSnapshot/SideBar/utils';
import { GLOBAL_IMAGES_ACTION_PATH } from 'pages/global-images/constants';
import { PREF_CARDS_BASE_ROUTE } from 'pages/preference-cards/basePath';
import { RECALLS_BASE_ROUTE } from 'pages/recalls/basepath';
import { RESILIENCY_MONITOR_BASE_ROUTE } from 'pages/resiliency-monitor/basePath';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route id="root" path="/" lazy={() => import('Root')}>
      {/* Redirects */}
      <Route path="/" lazy={() => import('components/RouteContainers/LandingRedirect')} />
      {/* Landing Page */}
      <Route path="/home" lazy={() => import('pages/home')} />
      {/* Auth / Profile */}
      <Route path="/reset-password" lazy={() => import('pages/ForgotPassword/routes/reset-password')}>
        <Route index lazy={() => import('pages/ForgotPassword/routes/reset-password.index')} />
        <Route path="confirm" lazy={() => import('pages/ForgotPassword/routes/reset-password.confirm')} />
        <Route path="success" lazy={() => import('pages/ForgotPassword/routes/reset-password.success')} />
      </Route>

      <Route path="/login" lazy={() => import('pages/Login/routes/login')}>
        <Route index lazy={() => import('pages/Login/routes/login.index')} />
        <Route path="sso" lazy={() => import('pages/Login/routes/login.sso')} />
        <Route path="callback" lazy={() => import('pages/Login/routes/login.callback')} />
      </Route>
      <Route path="/logout" lazy={() => import('pages/Logout')} />
      <Route path="/profile" lazy={() => import('pages/Profile')} />
      <Route path="/spoof" lazy={() => import('pages/Spoofing')} />
      <Route path="/admin" id="admin-root" lazy={() => import('pages/admin/routes/admin')}>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="users" lazy={() => import('pages/admin/routes/admin.users')}>
          <Route path="new" lazy={() => import('pages/admin/routes/admin.users.new')} />
          <Route path=":userId" id="admin-userId" lazy={() => import('pages/admin/routes/admin.users.$userId')}>
            <Route index lazy={() => import('pages/admin/routes/admin.users.$userId.user')} />
            <Route path="access" lazy={() => import('pages/admin/routes/admin.users.$userId.access')} />
            <Route path="deactivate" lazy={() => import('pages/admin/routes/admin.users.$userId.deactivate')} />
          </Route>
        </Route>
        <Route path="teams" lazy={() => import('pages/admin/routes/admin.teams')}>
          <Route path="new" lazy={() => import('pages/admin/routes/admin.teams.new')} />
          <Route path=":teamId" id="admin-teamId" lazy={() => import('pages/admin/routes/admin.teams.$teamId')}>
            <Route path="delete" lazy={() => import('pages/admin/routes/admin.teams.$teamId.delete')} />
          </Route>
        </Route>
        <Route
          path="configuration-manager"
          lazy={() => import('pages/admin/routes/configuration-manager/configuration-manager-layout.route')}
        >
          <Route
            index
            lazy={() => import('pages/admin/routes/configuration-manager/configuration-manager-index.route')}
          />
          <Route
            path="custom-task-statuses"
            lazy={() => import('pages/admin/routes/custom-task-statuses/custom-task-statuses.route')}
          />
          <Route
            path="custom-task-statuses/:taskTypeId"
            lazy={() => import('pages/admin/routes/custom-task-statuses/custom-task-statuses-task-type.route')}
          />
        </Route>
      </Route>
      <Route path="exceptions" lazy={() => import('pages/Exceptions/routes/exceptions')}>
        <Route index lazy={() => import('pages/Exceptions/routes/exceptions.index')} />
        <Route path=":poId" lazy={() => import('pages/Exceptions/routes/exceptions.$poId')} />
        <Route path="dashboard" lazy={() => import('pages/Exceptions/routes/exceptions.dashboard')} />
      </Route>
      <Route path="disruptions" id="drm-root" lazy={() => import('pages/Disruption/routes/disruptions')}>
        <Route index lazy={() => import('pages/Disruption/routes/disruptions.index')} />
        <Route path="overview" id="drm-overview" lazy={() => import('pages/Disruption/routes/disruptions.overview')} />
        <Route path="events" id="drm-events" lazy={() => import('pages/Disruption/routes/disruptions.events')} />
        <Route
          path="events/:eventId"
          id="drm-event-details"
          lazy={() => import('pages/Disruption/routes/disruptions.events.$eventId')}
        />
        <Route
          path=":disruptionId"
          id="drm-item"
          lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId')}
        >
          <Route
            index
            id="drm-item-params"
            lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.index')}
          />
          <Route path="inventory" lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.inventory')} />
          <Route
            path="preference-cards"
            lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.preference-cards')}
          />
          <Route
            path="utilization"
            lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.utilization')}
          />
          <Route path="vendors" lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.vendors')} />
          <Route
            path="substitutes"
            lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.substitutes')}
          >
            <Route
              path="request"
              lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.substitutes.request')}
            />
          </Route>
          <Route path="events" lazy={() => import('pages/Disruption/routes/disruptions.$disruptionId.events')} />

          <Route
            path={SIDE_BAR_ACTION_ROUTE}
            lazy={() => import('pages/Disruption/SupplyChainSnapshot/SideBar/sideBarAction')}
          />
        </Route>
      </Route>
      <Route path="substitutes" lazy={() => import('pages/substitutes/routes/substitutes')}>
        <Route index lazy={() => import('pages/substitutes/routes/substitutes.index')} />
        <Route
          path=":substituteId"
          id="substitute_id"
          lazy={() => import('pages/substitutes/routes/substitutes.$substituteId')}
        >
          <Route
            path=""
            id="substitute_id_index"
            lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.index')}
          >
            <Route path="request" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.request')} />
            <Route path="edit" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.edit')} />
            <Route path="approval" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.approval')} />
          </Route>
          <Route path="tasks" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.tasks')} />
        </Route>
        <Route path="overview" lazy={() => import('pages/substitutes/routes/substitutes.overview')} />
      </Route>
      <Route path="/create-substitute-request" lazy={() => import('pages/root/create-substitute-request')} />
      <Route
        path="external/substitutes/approvals/:requestId"
        lazy={() => import('pages/external/substitutes/approvals.route')}
      />
      <Route path={RECALLS_BASE_ROUTE} lazy={() => import('pages/recalls/routes/recalls')}>
        <Route index lazy={() => import('pages/recalls/routes/recalls.index')} />
        <Route id="recall-id" path=":recallId" lazy={() => import('pages/recalls/routes/recalls.$recallId')}>
          <Route index lazy={() => import('pages/recalls/routes/recalls.$recallId.index')} />
          <Route path="affected-items" lazy={() => import('pages/recalls/routes/recalls.$recallId.affected-items')} />
          <Route path="tasks" lazy={() => import('pages/recalls/routes/recalls.$recallId.tasks')} />
        </Route>
      </Route>
      <Route
        path={RESILIENCY_MONITOR_BASE_ROUTE}
        lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor')}
      >
        <Route index lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.index')} />
        <Route path="categories" lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.categories')} />
        <Route path="contracts" lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.contracts')} />
      </Route>
      <Route
        path="external/preference-cards/approvals/:requestApprovalId"
        lazy={() =>
          import('pages/external/preference-cards/routes/external.preference-cards.approvals.$requestApprovalId')
        }
      />
      <Route
        id="co-root"
        path={PREF_CARDS_BASE_ROUTE}
        lazy={() => import('pages/preference-cards/routes/preference-cards')}
      >
        <Route path="overview" lazy={() => import('pages/preference-cards/routes/preference-cards.overview')} />
        <Route path="*" lazy={() => import('pages/preference-cards/routes/queue-layout')}>
          <Route index lazy={() => import('pages/preference-cards/routes/preference-cards.index')} />
          <Route path="requests" lazy={() => import('pages/preference-cards/routes/preference-cards.requests')} />
          <Route
            path="recommendations"
            id="co-recommendations-queue"
            lazy={() => import('pages/preference-cards/routes/recommendations/co.recommendations')}
          />
          <Route
            id="co-procedure-queue"
            path="procedures"
            lazy={() => import('pages/preference-cards/routes/procedures/procedures')}
          />
        </Route>
        <Route
          path="recommendations/:recommendationId"
          lazy={() => import('pages/preference-cards/routes/recommendations/co.$recommendationId')}
        >
          <Route
            index
            lazy={() => import('pages/preference-cards/routes/recommendations/co.$recommendationId.index')}
          />
          <Route
            path="preference-cards"
            lazy={() => import('pages/preference-cards/routes/recommendations/co.$recommendationId.preference-cards')}
          />
        </Route>
        <Route
          id="procedure-card-detail"
          path="procedures/:procedureId"
          lazy={() => import('pages/preference-cards/routes/procedures/procedures.$procedureId')}
        >
          <Route index lazy={() => import('pages/preference-cards/routes/procedures/procedures.$procedureId.index')} />
          <Route
            path="preference-cards"
            lazy={() => import('pages/preference-cards/routes/procedures/procedures.$procedureId.preference-cards')}
          />
          <Route
            path="activity"
            lazy={() => import('pages/preference-cards/routes/procedures/procedures.$procedureId.activity')}
          />
        </Route>
        <Route
          path=":cardId"
          id="co-card"
          lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId')}
        >
          <Route
            id="co-card-items"
            index
            lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId.index')}
          />
          <Route
            path="requests"
            lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId.requests')}
          />

          <Route path="tasks" lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId.tasks')} />
          <Route
            id="co-card-standardization"
            path="standardization"
            lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId.standardization')}
          />
        </Route>
        <Route
          path=":cardId/requests/:requestId"
          lazy={() => import('pages/preference-cards/routes/preference-cards.$cardId.requests.$requestId')}
        />
        <Route path="ch-data" lazy={() => import('pages/preference-cards/routes/preference-cards.ch-data')} />
      </Route>
      <Route path="task-manager" id="tsk-root" lazy={() => import('pages/task-manager/routes/task-manager')}>
        <Route index id="tsk-index" lazy={() => import('pages/task-manager/routes/task-manager.index')} />
        <Route
          path="overview"
          id="tsk-overview"
          lazy={() => import('pages/task-manager/routes/task-manager.overview')}
        />
        <Route path=":taskId" id="tsk-id" lazy={() => import('pages/task-manager/routes/task-manager.$taskId')} />
      </Route>
      <Route path={GLOBAL_IMAGES_ACTION_PATH} lazy={() => import('pages/global-images/routes/global-images')} />
      <Route path="*" lazy={() => import('pages/NotFound')} />
      <Route path="oculus" lazy={() => import('pages/oculus/routes/oculus')}>
        <Route index lazy={() => import('pages/oculus/routes/oculus.index')} />
        <Route path=":threadId" lazy={() => import('pages/oculus/routes/oculus.$threadId')} />
      </Route>
      <Route path="/resources/tasks" lazy={() => import('pages/resources/tasks/tasks.route')} />
    </Route>
  )
);

export default router;
